import { useCallback } from "react";
import { useQuery, useQueryClient } from "react-query";
import { sortBy } from "lodash";
import axios from "axios";
import {
  ChartLine,
  CircleDollarSign,
  Crosshair,
  Ellipsis,
  Funnel,
  Layers,
  Route,
  TrendingUp,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { makeRequest } from "@/util";
import { API_V2_ROOT } from "@/config";
import { apiV2AuthHeaders } from "@/api/v2Headers";
import { getPreviewHandler, isPreviewModeEnabled } from "@/preview-mode";
import {
  ENDPOINTS,
  MULTI_TOUCH_ATTRIBUTIONS,
  JOURNEY_OPTIONS,
} from "@/InsightsPage/constants";

export type TotalConversionsResponse = {
  conversionRate: number;
  total: number;
  clicks?: number;
  impressions?: number;
  spend?: number;
  roas?: number;
};

type PerformanceType = {
  count: number;
  key: string;
  name: string;
};

export type DateRangeRequest = {
  dateRange: {
    date_from: string;
    date_to?: string;
    clientTimeZone: string;
  };
};

export type TouchBreakdownRequest = DateRangeRequest & {
  sourceKey: string;
};

export type FunnelStep = {
  eventName: string;
};

export type FunnelQueryRequest = DateRangeRequest & {
  steps: FunnelStep[];
  timeWindowSecs?: number;
};

export type FunnelStepResult = {
  stepIndex: number;
  stepName: string;
  count: number;
  conversionRate: number;
  dropoffPct: number;
};

export const ATTRIBUTION_MODELS = [
  MULTI_TOUCH_ATTRIBUTIONS.LAST_TOUCH.key,
  MULTI_TOUCH_ATTRIBUTIONS.FIRST_TOUCH.key,
  MULTI_TOUCH_ATTRIBUTIONS.LINEAR.key,
  MULTI_TOUCH_ATTRIBUTIONS.U_SHAPED.key,
  MULTI_TOUCH_ATTRIBUTIONS.J_SHAPED.key,
] as const;

export type AttributionModel = (typeof ATTRIBUTION_MODELS)[number];

export const CHANNEL_JOURNEY_OPTIONS = [
  JOURNEY_OPTIONS.MULTI.key,
  JOURNEY_OPTIONS.SINGLE.key,
  JOURNEY_OPTIONS.ALL.key,
] as const;

export type ChannelJourneyModel = (typeof CHANNEL_JOURNEY_OPTIONS)[number];

export type SourcePerformanceResponse = {
  bottomFunnelConversions: PerformanceType[];
  clicks: PerformanceType[];
  conversionRate: number;
  impressions: PerformanceType[];
  midFunnelConversions: PerformanceType[];
};

export type SourceAnalysis = {
  dimension: "source" | "campaign";
  key: string;
  type: string | null;
  label: string;
  clicks: number;
  impressions: number | null;
  spend: number | null;
  numMidFunnelConversions: number;
  numBottomFunnelConversions: number;
  customerAcquisitionCost: number | null;
  children?: SourceAnalysis[];
};

export type SourceAnalysisLevel = SourceAnalysis["dimension"];

export type InsightsOverviewJourney =
  | "all_journeys"
  | "single_source_journeys"
  | "multi_source_journeys";
export type InsightsOverviewAttributionModel =
  | "first_touch"
  | "last_touch"
  | "linear"
  | "u_shaped"
  | "j_shaped";

export type InsightsOverviewSourceAnalysisData = {
  levels: readonly SourceAnalysisLevel[];
  nodes: SourceAnalysis[];
};

type InsightsOverviewJourneyData<TData> = Record<
  InsightsOverviewJourney,
  TData
>;

type InsightsOverviewEndpointData = {
  source_analysis: InsightsOverviewJourneyData<InsightsOverviewSourceAnalysisData>;
  source_performance: InsightsOverviewJourneyData<SourcePerformanceResponse>;
  total_conversions: InsightsOverviewJourneyData<TotalConversionsResponse>;
};

export type SourceAnalysisResponse = SourceAnalysis & {
  _isChildRow?: boolean; // Internal flag for UI grouping
  _parentKey?: string; // Internal flag for UI grouping
  _parentLabel?: string; // Internal flag for UI grouping
  _originalKey?: string; // Internal flag for UI grouping
  _hasChildren?: boolean; // Internal flag for UI grouping
  _isInfoRow?: boolean; // Internal flag for full-width informational rows
  _isPendingDataRow?: boolean; // Internal flag for pending campaign support rows
  _isChildrenToggleRow?: boolean; // Internal flag for campaign show more rows
  _isExpandedRow?: boolean; // Internal flag for parent row expansion state
  _matchingChildrenSortRow?: SourceAnalysisResponse; // Internal flag for search-result sorting
  _campaignCount?: number; // Internal flag for filtered campaign badge count
  _childrenToggleParentKey?: string; // Internal flag for campaign show more rows
  _hiddenChildrenCount?: number; // Internal flag for campaign show more rows
  _isShowingAllChildren?: boolean; // Internal flag for campaign show more rows
};

export type IntegrationStatsPullRequest = {
  destinationId?: string;
  platformName?: string;
};

export type IntegrationStatsPullResponse = {
  newestDateAvailable: string;
  oldestDateAvailable: string;
  status: "UP_TO_DATE" | "UNINITIALIZED" | "RUNNING" | "ERROR" | "DONT_SHOW";
  whenPulled: string;
  errorMessage?: string;
};

export type FunnelEvents = {
  eventDefId: string;
  name: string;
  funnelStage: "MidFunnel" | "BottomFunnel" | "";
  isRecommended: boolean;
  isOther: boolean;
  isHidden: boolean;
  description: string;
  totalEventsDelivered: number;
  isHighVolumeEvent: boolean;
  adPlatformsDelivered: number;
  adPlatformsDeliveredList: string;
  isSpecialCaseAdsDelivPv: boolean;
  hasNoRecentTraffic: boolean;
  tags: Record<string, true>;
  type: string;
};

export type DynamicEventsResponse = {
  bottomFunnel: FunnelEvents[];
  midFunnel: FunnelEvents[];
};

export type EnvConfig = {
  isDemo: boolean;
  name: string;
  envId: string;
  groupingEnabled: boolean;
  metricsCardsEnabled: boolean;
  aiConsent: boolean;
};

export type GetEnvConfigResponse = {
  envConfig: EnvConfig;
};

export type InsightsOverviewResponse = Record<
  InsightsOverviewAttributionModel,
  InsightsOverviewEndpointData
>;
type Timestamp = string;

export type DateRange = {
  begin: Timestamp;
  end: Timestamp;
};

export type SupportingMetrics = {
  dateRange: DateRange;
  definition: string;
  metric: string;
  value: string;
};

export type CardFeedbackState = {
  sentiment: "positive" | "negative";
  hasComment: boolean;
};

export const RECOMMENDATION_CATEGORIES = [
  "Budget",
  "Portfolio",
  "Channel Role",
  "Channel Performance",
  "Attribution",
  "Funnel & Journey",
  "Trend",
  "Other",
] as const;

export type RecommendationCategory = (typeof RECOMMENDATION_CATEGORIES)[number];

export const RECOMMENDATION_CATEGORY_BADGE_CONFIG: Record<
  RecommendationCategory,
  {
    label: RecommendationCategory;
    backgroundColor: string;
    color: string;
    Icon?: LucideIcon;
  }
> = {
  Budget: {
    label: "Budget",
    backgroundColor: "bg-blue-100",
    color: "text-blue-600",
    Icon: CircleDollarSign,
  },
  Portfolio: {
    label: "Portfolio",
    backgroundColor: "bg-purple-50",
    color: "text-purple-500",
    Icon: Layers,
  },
  "Channel Role": {
    label: "Channel Role",
    backgroundColor: "bg-yellow-100",
    color: "text-yellow-800",
    Icon: Route,
  },
  "Channel Performance": {
    label: "Channel Performance",
    backgroundColor: "bg-green-100",
    color: "text-green-800",
    Icon: TrendingUp,
  },
  Attribution: {
    label: "Attribution",
    backgroundColor: "bg-red-100",
    color: "text-red-700",
    Icon: Crosshair,
  },
  "Funnel & Journey": {
    label: "Funnel & Journey",
    backgroundColor: "bg-blue-50",
    color: "text-blue-700",
    Icon: Funnel,
  },
  Trend: {
    label: "Trend",
    backgroundColor: "bg-green-50",
    color: "text-green-700",
    Icon: ChartLine,
  },
  Other: {
    label: "Other",
    backgroundColor: "bg-gray-100",
    color: "text-gray-500",
    Icon: Ellipsis,
  },
};

export type RecommendationCardResponse = {
  cardId: string;
  envId: string;
  accountId: string;
  cardRank: number;
  generatedAt: Timestamp;
  dateRangeEnd: Timestamp;
  timeWindowUsed: "30d" | "7d";
  attributionModelUsed:
    | "last_touch"
    | "first_touch"
    | "linear"
    | "u_shaped"
    | "j_shaped";
  attributionModelReason: string;
  title: string;
  teaser: string;
  fullRecommendation: string;
  shortRecommendation: string;
  supportingMetrics: SupportingMetrics[];
  changeStatus: "new" | "continued" | "reversed" | "resolved";
  changeNote: string;
  dataQualityLabel: "High" | "Medium" | "Low";
  dataQualityReason: string;
  category?: RecommendationCategory;
  feedback?: CardFeedbackState;
};

export type SubmitCardFeedbackRequest = {
  cardId: string;
  sentiment: "positive" | "negative";
  comment?: string;
};

export type SubmitCardFeedbackResponse = {
  feedback: CardFeedbackState;
};

export type RecommendationCardsResponse = {
  envId: string;
  state:
    | "ok"
    | "error"
    | "need_ai_consent"
    | "need_conversion_events"
    | "waiting_for_generation"
    | "not_enough_recent_data";
  cards: RecommendationCardResponse[];
  updatedAt: Timestamp;
};

type TimeSeries = {
  periodStart: Timestamp;
  value: number;
};

type TouchBreakdownMetrics = {
  type: string;
  currentTotal: number;
  timeSeries: TimeSeries[];
};

export type TouchBreakdownResponse = {
  dimension: "source" | "campaign";
  key: string;
  label: string;
  metrics: TouchBreakdownMetrics[];
  children?: TouchBreakdownResponse[];
};

export function useDynamicEvents() {
  const queryClient = useQueryClient();

  return useCallback(
    async (payload: DynamicEventsResponse) => {
      const response = await makeRequest(ENDPOINTS.SET_DYNAMIC_EVENTS, payload);

      // Invalidate dynamic events and all dependent queries
      await queryClient.invalidateQueries([ENDPOINTS.GET_DYNAMIC_EVENTS]);
      await queryClient.invalidateQueries([ENDPOINTS.GET_INSIGHTS_OVERVIEW]);

      return response.data;
    },
    [queryClient],
  );
}

export function useInsightsEventDefs() {
  return useQuery<FunnelEvents[]>(
    [ENDPOINTS.GET_EVENT_DEFS_ENRICHED],
    async () => {
      const response = await makeRequest(ENDPOINTS.GET_EVENT_DEFS_ENRICHED, {});
      const data = (response.data as FunnelEvents[] | null) ?? [];
      return sortBy(data, (e) => e.name.toLowerCase());
    },
  );
}

// fetchInsightsOverview calls the v2 endpoint
// (GET /v2/internal/insights/overview). It uses axios so failures surface as
// AxiosErrors, matching what InsightsContext's isServerError/handleApiError
// (and the other still-v1 endpoints in that provider) expect.
//
// The environment is sent via the X-Env-ID header (InternalAuth resolves and
// authorizes it server-side), not a query param. Masquerade is forwarded via
// X-Masquerade-Token. Note the canonical "clientTimezone" query key — v1's body
// used the miscased "clientTimeZone", which only worked via case-insensitive
// JSON tag matching.
export async function fetchInsightsOverview(
  dateRange: DateRangeRequest["dateRange"],
): Promise<InsightsOverviewResponse> {
  if (isPreviewModeEnabled()) {
    const handler = getPreviewHandler(ENDPOINTS.GET_INSIGHTS_OVERVIEW);
    if (handler) {
      return handler({ dateRange }) as InsightsOverviewResponse;
    }
  }

  const params = new URLSearchParams();
  params.set("date_from", dateRange.date_from);
  if (dateRange.date_to) {
    params.set("date_to", dateRange.date_to);
  }
  params.set("clientTimezone", dateRange.clientTimeZone);

  const headers = await apiV2AuthHeaders();

  const response = await axios.get<InsightsOverviewResponse>(
    `${API_V2_ROOT}/v2/internal/insights/overview?${params.toString()}`,
    { headers },
  );
  return response.data;
}

export async function fetchInsightsSourceTouchBreakdown({
  sourceKey,
  dateRange,
}: TouchBreakdownRequest): Promise<TouchBreakdownResponse> {
  if (isPreviewModeEnabled()) {
    const handler = getPreviewHandler(
      ENDPOINTS.GET_INSIGHTS_SOURCE_TOUCH_BREAKDOWN,
    );
    if (handler) {
      return handler({ sourceKey, dateRange }) as TouchBreakdownResponse;
    }
  }

  const params = new URLSearchParams();
  params.set("date_from", dateRange.date_from);
  if (dateRange.date_to) {
    params.set("date_to", dateRange.date_to);
  }
  params.set("clientTimezone", dateRange.clientTimeZone);

  const headers = await apiV2AuthHeaders();

  const response = await axios.get<TouchBreakdownResponse>(
    `${API_V2_ROOT}/v2/internal/insights/sources/${encodeURIComponent(sourceKey)}/touch_breakdown?${params.toString()}`,
    { headers },
  );
  return response.data;
}
